import React, { useEffect } from 'react'
import {
  Grid,
  Paper,
  Box,
  Typography,
  Container,
  LinearProgress,
  Card,
  CardContent
} from '@mui/material'
import {
  FolderOpen,
  Warning,
  CheckCircle,
  Archive,
  AccessTime,
  TrendingUp,
  Business,
  Folder,
  CalendarMonth,
  BugReport,
  Groups
} from '@mui/icons-material'
import { useAuth } from '../contexts/Authcontexts'
import { useMRD } from '../contexts/Mrdcontexts'
import StatCard from '../components/mrd/dashboard/statcard'
import DepartmentChart from '../components/mrd/dashboard/DepartmentChart'
import RecentActivity from '../components/mrd/dashboard/RecentActivity'
import { useSnackbar } from 'notistack'

export const DashboardPage = () => {
  const { stats, masterDataStats, fetchDashboardStats, fetchMasterDataStats, loading } = useMRD()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    loadDashboardData()
    // Refresh every 5 minutes
    const interval = setInterval(loadDashboardData, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  const loadDashboardData = async () => {
    try {
      await fetchDashboardStats()
      await fetchMasterDataStats()
    } catch (error) {
      enqueueSnackbar('Failed to load dashboard data', { variant: 'error' })
    }
  }

  const fileStats = stats?.fileStatistics || {}
  const monthlyStats = stats?.monthlyStatistics || {}
  const deficiencyStats = stats?.deficiencyStatistics || {}
  const tatMetrics = stats?.tatMetrics || {}
  const departmentData = stats?.departmentDistribution || []
  const statusBreakdown = stats?.statusBreakdown || []

  const masterSummary = masterDataStats?.summary || {}
  const icdStats = masterDataStats?.icdStatistics || {}
  const doctorStats = masterDataStats?.doctorStatistics || {}
  const wardStats = masterDataStats?.wardStatistics || {}
  const deficiencyMasterStats = masterDataStats?.deficiencyStatistics || {}
  const recentActivity = masterDataStats?.recentActivity?.last30Days || {}
  const healthMetrics = masterDataStats?.healthMetrics || {}

  // Calculate completion rate from file statistics
  const completionRate = fileStats.total > 0 
    ? ((fileStats.completed / fileStats.total) * 100).toFixed(2)
    : "0.00"

  // Calculate deficiency resolution rate
  const deficiencyResolutionRate = deficiencyStats.total > 0
    ? ((deficiencyStats.resolvedThisMonth / deficiencyStats.total) * 100).toFixed(2)
    : "0.00"

  return (
    <Container maxWidth="xl" sx={{ mt: 0 }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Welcome back, {user?.name?.split(' ')[0] || 'User'}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your MRD department today.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      ) : (
        <>
          {/* File Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Files"
                value={fileStats.total || 0}
                color="primary"
                icon={<Folder />}
                subtitle="All MRD files"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active Files"
                value={fileStats.active || 0}
                color="info"
                icon={<FolderOpen />}
                subtitle="Currently being processed"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Completed Files"
                value={fileStats.completed || 0}
                color="success"
                icon={<CheckCircle />}
                subtitle="Finalized this month"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Archived Files"
                value={fileStats.archived || 0}
                color="secondary"
                icon={<Archive />}
              />
            </Grid>
          </Grid>

          {/* Monthly & Deficiency Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="New Files This Month"
                value={monthlyStats.newFiles || 0}
                color="info"
                icon={<CalendarMonth />}
                subtitle="Monthly intake"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Completion Rate"
                value={`${monthlyStats.completionRate || "0.00"}%`}
                color="success"
                icon={<TrendingUp />}
                subtitle="Monthly performance"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Open Deficiencies"
                value={deficiencyStats.open || 0}
                color="warning"
                icon={<Warning />}
                subtitle="Requiring attention"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Deficiency Resolution"
                value={`${deficiencyResolutionRate}%`}
                color="error"
                icon={<BugReport />}
                subtitle="Resolved this month"
              />
            </Grid>
          </Grid>

          {/* TAT Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <AccessTime sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  {tatMetrics.average || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average TAT (Days)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <AccessTime sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  {tatMetrics.maximum || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximum TAT (Days)
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <AccessTime sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  {tatMetrics.minimum || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Minimum TAT (Days)
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Master Data Statistics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Master Data Overview
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: 'primary.main'
                      }
                    }}>
                      <Typography variant="h4" color="primary" fontWeight={600}>
                        {masterSummary.activeICDCodes || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active ICD Codes
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {masterSummary.totalICDCodes || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: 'success.main'
                      }
                    }}>
                      <Typography variant="h4" color="success.main" fontWeight={600}>
                        {masterSummary.activeDoctors || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Doctors
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {masterSummary.totalDoctors || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: 'info.main'
                      }
                    }}>
                      <Typography variant="h4" color="info.main" fontWeight={600}>
                        {masterSummary.activeWards || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Wards
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {masterSummary.totalWards || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: 'warning.main'
                      }
                    }}>
                      <Typography variant="h4" color="warning.main" fontWeight={600}>
                        {masterSummary.activeDeficiencyMasters || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Deficiency Types
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {masterSummary.totalDeficiencyMasters || 0}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: 2,
                        borderColor: 'secondary.main'
                      }
                    }}>
                      <Typography variant="h4" color="secondary.main" fontWeight={600}>
                        {masterSummary.activeDepartments || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Departments
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {masterSummary.totalDepartments || 0}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Health Metrics */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" color="primary.main" fontWeight={500}>
                          ICD Active Rate: 
                        </Box> {healthMetrics.icdActiveRate || 0}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" color="success.main" fontWeight={500}>
                          Doctor Active Rate: 
                        </Box> {healthMetrics.doctorActiveRate || "0.00"}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" color="info.main" fontWeight={500}>
                          Ward Active Rate: 
                        </Box> {healthMetrics.wardActiveRate || "0.00"}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" color="warning.main" fontWeight={500}>
                          Critical Deficiencies: 
                        </Box> {healthMetrics.criticalDeficiencyRate || "0.00"}%
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {/* Recent Activity Summary */}
                  {masterDataStats?.recentActivity?.totalRecentAdditions > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="success.main">
                        <strong>Recent Activity:</strong> {masterDataStats.recentActivity.totalRecentAdditions} new entries in last 30 days
                        {recentActivity.icdCodes > 0 && ` • ICD: ${recentActivity.icdCodes}`}
                        {recentActivity.doctors > 0 && ` • Doctors: ${recentActivity.doctors}`}
                        {recentActivity.wards > 0 && ` • Wards: ${recentActivity.wards}`}
                        {recentActivity.deficiencies > 0 && ` • Deficiencies: ${recentActivity.deficiencies}`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts and Activity */}
          <Grid container spacing={3}>
            {/* Department Distribution */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Department-wise File Distribution
                </Typography>
                {departmentData.length > 0 ? (
                  <Box sx={{ height: 400, mt: 2 }}>
                    <DepartmentChart data={departmentData} />
                  </Box>
                ) : (
                  <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No department data available
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Status Breakdown */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  File Status Breakdown
                </Typography>
                {statusBreakdown.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {statusBreakdown.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' }
                        }}
                      >
                        <Typography variant="body2">
                          {item._id || 'Unspecified Status'}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {item.count}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No status data available
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Create New MRD File', path: '/hospital/mrd/mrd-files' },
                    { label: 'Manage Deficiencies', path: '/hospital/mrd/deficiencies' },
                    { label: 'Upload Documents', path: '/hospital/mrd/documents' },
                    { label: 'Process Coding', path: '/hospital/mrd/coding' },
                    { label: 'Close Files', path: '/hospital/mrd/closure' },
                    { label: 'View Reports', path: '/hospital/mrd/reports' },
                    { label: 'Manage Master Data', path: '/hospital/mrd/master-data' },
                    { label: 'Department Overview', path: '/hospital/mrd/departments' }
                  ].map((action) => (
                    <Grid item key={action.label}>
                      <Card
                        sx={{
                          minWidth: 180,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 6
                          }
                        }}
                        onClick={() => window.location.pathname = action.path}
                      >
                        <CardContent>
                          <Typography variant="body2" color="primary" fontWeight={500}>
                            {action.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Deficiency Statistics */}
          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Deficiency Overview
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2
                    }}>
                      <Typography variant="h4" fontWeight={600}>
                        {deficiencyStats.total || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Deficiencies
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      textAlign: 'center', 
                      p: 2, 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 2
                    }}>
                      <Typography variant="h4" color="success.main" fontWeight={600}>
                        {deficiencyStats.resolvedThisMonth || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resolved This Month
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Master Deficiency Stats */}
                {deficiencyMasterStats.categoryBreakdown?.length > 0 && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Deficiency Types by Category:
                    </Typography>
                    {deficiencyMasterStats.categoryBreakdown.map((category) => (
                      <Box
                        key={category._id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1
                        }}
                      >
                        <Typography variant="body2">
                          {category._id}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total: {category.count}
                          </Typography>
                          {category.criticalCount > 0 && (
                            <Typography variant="body2" color="error">
                              Critical: {category.criticalCount}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Today's Targets */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Performance Targets
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {[
                    { 
                      label: 'Monthly File Completion', 
                      target: 100, 
                      current: parseInt(monthlyStats.completed || 0),
                      color: 'primary' 
                    },
                    { 
                      label: 'Deficiency Resolution', 
                      target: deficiencyStats.total || 1, 
                      current: deficiencyStats.resolvedThisMonth || 0,
                      color: 'success' 
                    },
                    { 
                      label: 'New Files Processed', 
                      target: 150, 
                      current: monthlyStats.newFiles || 0,
                      color: 'info' 
                    },
                    { 
                      label: 'TAT Compliance (<30 days)', 
                      target: 100, 
                      current: tatMetrics.average < 30 ? 100 : 0,
                      color: 'warning' 
                    }
                  ].map((item) => (
                    <Box key={item.label} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{item.label}</Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {item.current}/{item.target}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((item.current / item.target) * 100, 100)}
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: `${item.color}.light`,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: `${item.color}.main`
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  )
}